/**
 * 🆔 Phase 14.0.1 — Build/Version stamp card.
 *
 * Surfaces the running deployment identity inside Admin Tools so the
 * operator can confirm WHICH build is live after every redeploy. Saves
 * 20 min of guesswork every time a "is this the new build?" question
 * comes up.
 *
 * Reads GET /api/version (public, no auth — see version_routes.py).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Row = ({ label, value, mono = true, testId }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1">
    <span className="text-gray-400 text-xs uppercase tracking-wider">{label}</span>
    <span
      className={`text-white text-sm break-all ${mono ? "font-mono" : ""}`}
      data-testid={testId}
    >
      {value ?? "—"}
    </span>
  </div>
);

const PhaseDot = ({ live, label, testId }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border ${
      live
        ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
        : "bg-red-500/15 text-red-200 border-red-500/30"
    }`}
    data-testid={testId}
  >
    <span>{live ? "✓" : "✗"}</span>
    <span>{label}</span>
  </span>
);

const BuildVersionStamp = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVersion = async () => {
    setLoading(true);
    try {
      const r = await api.get("/version");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to fetch /api/version");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersion();
  }, []);

  const phaseMap = data?.phase_markers || {};
  const phases = [
    { key: "phase_11_apply_promo_tier", label: "Phase 11 (Promo tiers)" },
    { key: "phase_13_share_canonical", label: "Phase 13 (Share OG)" },
    { key: "phase_13_og_alias", label: "Phase 13 (OG crawler alias)" },
    { key: "phase_131_audit_single", label: "Phase 13.1 (Per-track audit)" },
    { key: "phase_14_recap_canonical", label: "Phase 14 (Decoration sweep)" },
  ];

  const copy = (s) => {
    if (!s) return;
    navigator.clipboard?.writeText(s);
    toast.success("Copied");
  };

  return (
    <Card
      className="p-4 bg-gradient-to-br from-slate-900/60 to-black/40 border-slate-500/30 mb-4"
      data-testid="build-version-stamp"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white font-bold flex items-center gap-2">
          <span>🆔</span>
          <span>Build / Version Stamp</span>
        </h4>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-500/40 text-white text-xs"
            onClick={fetchVersion}
            disabled={loading}
            data-testid="build-stamp-refresh"
          >
            {loading ? "Refreshing…" : "🔄 Refresh"}
          </Button>
          {data && (
            <Button
              size="sm"
              variant="outline"
              className="border-slate-500/40 text-white text-xs"
              onClick={() => copy(JSON.stringify(data, null, 2))}
              data-testid="build-stamp-copy"
            >
              📋 Copy JSON
            </Button>
          )}
        </div>
      </div>

      {loading && !data && (
        <p className="text-gray-400 text-sm">Loading deploy identity…</p>
      )}

      {data && (
        <>
          <div className="space-y-0">
            <Row
              label="Git SHA"
              value={
                <span className="inline-flex items-center gap-2">
                  <span>{data.git_sha}</span>
                  <button
                    onClick={() => copy(data.git_sha)}
                    className="text-slate-400 hover:text-white text-xs"
                    title="Copy SHA"
                  >
                    📋
                  </button>
                </span>
              }
              testId="build-stamp-git-sha"
            />
            <Row label="Commit subject" value={data.git_subject} mono={false} testId="build-stamp-subject" />
            <Row label="Committed at" value={data.git_committed_at} testId="build-stamp-committed-at" />
            <Row label="Backend mtime" value={data.backend_mtime} testId="build-stamp-backend-mtime" />
            <Row label="Process started" value={data.process_started_at} testId="build-stamp-process-started" />
            <Row label="Server now" value={data.server_now} testId="build-stamp-server-now" />
            <Row label="MongoDB" value={data.mongo_db} testId="build-stamp-mongo-db" />
            <Row label="Environment" value={data.env} testId="build-stamp-env" />
          </div>

          <div className="mt-4 pt-3 border-t border-slate-500/20">
            <div className="text-gray-400 text-xs uppercase tracking-wider mb-2">
              Phase markers (server module symbols)
            </div>
            <div className="flex flex-wrap gap-2" data-testid="build-stamp-phase-markers">
              {phases.map((p) => (
                <PhaseDot
                  key={p.key}
                  live={!!phaseMap[p.key]}
                  label={p.label}
                  testId={`phase-marker-${p.key}`}
                />
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              ✓ = symbol present in the running server module · ✗ = missing
              (deploy may be incomplete or backend on an older bundle).
            </p>
          </div>
        </>
      )}
    </Card>
  );
};

export default BuildVersionStamp;
